import { useMemo, useState } from 'react'
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@/components/ui/sheet'
import {
  ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight,
  Clock, XCircle, AlertCircle,
} from 'lucide-react'
import {
  type GHLFollowUpRow,
  formatRelativeTime,
  formatFireAt,
} from '@/lib/ghl-dashboard'
import { useGHLContactNames } from '@/hooks/useGHLDashboard'
import { ContactAvatar } from './ContactAvatar'

interface Props {
  /** Pending step-99 grace rows — sorted by fire_at ASC (next-to-fire first). */
  readonly awaitingRows: readonly GHLFollowUpRow[]
  readonly awaitingLoading: boolean

  /** Sent step-99 rows (history of opportunities that became lost). */
  readonly historyRows: readonly GHLFollowUpRow[]
  readonly historyCount: number
  readonly historyPage: number
  readonly historyPageSize: number
  readonly historyLoading: boolean
  readonly onHistoryPageChange: (page: number) => void
}

/**
 * Mark-Lost dashboard view.
 *
 * Two sections, both backed by `ghl_followup_schedule` step-99 rows
 * (MARK_LOST_GRACE_STEP):
 *
 *   1) Awaiting Mark-Lost — opportunities currently in the grace window.
 *      The cron will fire the lost-status write when each row's `fire_at`
 *      passes — UNLESS the customer shows new booking intent first, in
 *      which case the row is auto-cancelled (cancel-path; not shown here).
 *   2) Marked Lost — opportunities whose grace window already expired and
 *      the lost-status write actually committed to GHL.
 *
 * Note: this view only covers the deferred grace path. If a location has
 * `mark_lost_grace_period_hours = 0`, `executeMarkLost` writes the lost
 * status directly without inserting a step-99 row, and those opportunities
 * don't appear here.
 */
export function MarkLostTab({
  awaitingRows,
  awaitingLoading,
  historyRows,
  historyCount,
  historyPage,
  historyPageSize,
  historyLoading,
  onHistoryPageChange,
}: Props) {
  return (
    <div className="space-y-6">
      <AwaitingSection rows={awaitingRows} isLoading={awaitingLoading} />
      <HistorySection
        rows={historyRows}
        count={historyCount}
        page={historyPage}
        pageSize={historyPageSize}
        isLoading={historyLoading}
        onPageChange={onHistoryPageChange}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Section 1: awaiting mark-lost (grace window)
// ---------------------------------------------------------------------------

function AwaitingSection({
  rows,
  isLoading,
}: {
  rows: readonly GHLFollowUpRow[]
  isLoading: boolean
}) {
  const contactIds = useMemo(
    () => [...new Set(rows.map((r) => r.contact_id).filter(Boolean))],
    [rows],
  )
  const effectiveLocationId = rows[0]?.location_id
  const { data: contactNames } = useGHLContactNames(contactIds, effectiveLocationId)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Clock className="h-4 w-4" />
          Awaiting Mark-Lost
          {rows.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              {rows.length} in grace
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Opportunities Claude detected as declined. Each row will be auto-marked as lost when its grace
          window expires — unless the customer shows new booking intent first, in which case the row is cancelled.
          The grace window length is per-location (default 24h, settable in Location Settings).
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Contact</TableHead>
                <TableHead>Fires</TableHead>
                <TableHead>Opportunity</TableHead>
                <TableHead>Decline reason</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={`skel-${i}`}>
                  {Array.from({ length: 5 }).map((_, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))}
              {!isLoading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    No opportunities in the grace window — nothing pending mark-lost.
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && rows.map((row) => {
                const name = contactNames?.get(row.contact_id)
                const fireAtMs = new Date(row.fire_at).getTime()
                const overdue = fireAtMs < Date.now()
                return (
                  <TableRow key={row.id}>
                    <TableCell>
                      <ContactAvatar
                        size="sm"
                        showName
                        firstName={name?.firstName}
                        lastName={name?.lastName}
                        fullName={name?.fullName}
                        contactId={row.contact_id}
                      />
                    </TableCell>
                    <TableCell>
                      {overdue ? (
                        <span className="flex items-center gap-1 text-amber-600 font-medium text-sm">
                          <AlertCircle className="h-3.5 w-3.5" />
                          overdue
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">{formatFireAt(row.fire_at)}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs font-mono text-muted-foreground truncate max-w-[200px]">
                      {row.opportunity_id ?? <span className="text-muted-foreground/40">--</span>}
                    </TableCell>
                    <TableCell className="text-sm max-w-[280px]">
                      {row.sms_prompt_context ? (
                        <p className="truncate text-muted-foreground" title={row.sms_prompt_context}>
                          {row.sms_prompt_context}
                        </p>
                      ) : <span className="text-muted-foreground/40">--</span>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {formatRelativeTime(row.created_at)}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Auto-refreshing every 15 seconds.</p>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Section 2: marked-lost history
// ---------------------------------------------------------------------------

function HistorySection({
  rows,
  count,
  page,
  pageSize,
  isLoading,
  onPageChange,
}: {
  rows: readonly GHLFollowUpRow[]
  count: number
  page: number
  pageSize: number
  isLoading: boolean
  onPageChange: (page: number) => void
}) {
  const [selectedRow, setSelectedRow] = useState<GHLFollowUpRow | null>(null)
  const totalPages = Math.max(1, Math.ceil(count / pageSize))
  const from = page * pageSize + 1
  const to = Math.min((page + 1) * pageSize, count)

  const contactIds = useMemo(
    () => [...new Set(rows.map((r) => r.contact_id).filter(Boolean))],
    [rows],
  )
  const effectiveLocationId = rows[0]?.location_id
  const { data: contactNames } = useGHLContactNames(contactIds, effectiveLocationId)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <XCircle className="h-4 w-4" />
          Marked Lost
          {count > 0 && (
            <Badge variant="secondary" className="text-xs">
              {count.toLocaleString()} in range
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Opportunities that were marked as lost after their grace window expired. Filtered by the dashboard
          time range and ordered by when the lost-status write actually fired.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Contact</TableHead>
                <TableHead>Opportunity</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Marked lost</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`skel-${i}`}>
                  {Array.from({ length: 4 }).map((_, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))}
              {!isLoading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                    No opportunities marked as lost in this period.
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && rows.map((row) => {
                const name = contactNames?.get(row.contact_id)
                return (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setSelectedRow(row)}
                  >
                    <TableCell>
                      <ContactAvatar
                        size="sm"
                        showName
                        firstName={name?.firstName}
                        lastName={name?.lastName}
                        fullName={name?.fullName}
                        contactId={row.contact_id}
                      />
                    </TableCell>
                    <TableCell className="text-xs font-mono text-muted-foreground truncate max-w-[200px]">
                      {row.opportunity_id ?? <span className="text-muted-foreground/40">--</span>}
                    </TableCell>
                    <TableCell className="text-sm max-w-[280px]">
                      {row.sms_prompt_context ? (
                        <p className="truncate text-muted-foreground" title={row.sms_prompt_context}>
                          {row.sms_prompt_context}
                        </p>
                      ) : <span className="text-muted-foreground/40">--</span>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {row.last_attempt_at ? formatRelativeTime(row.last_attempt_at) : '--'}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>

        {count > 0 && (
          <div className="flex items-center justify-between pt-3">
            <p className="text-sm text-muted-foreground">
              Showing {from}-{to} of {count.toLocaleString()}
            </p>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => onPageChange(0)}>
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => onPageChange(page - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm px-2">Page {page + 1} of {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => onPageChange(page + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => onPageChange(totalPages - 1)}>
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      {/* Detail sheet for inspecting a single marked-lost row */}
      <Sheet open={selectedRow !== null} onOpenChange={(open) => !open && setSelectedRow(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Marked-lost detail</SheetTitle>
          </SheetHeader>
          {selectedRow && (
            <div className="space-y-4 mt-4 text-sm">
              <DetailRow label="Contact ID" value={selectedRow.contact_id} mono />
              <DetailRow label="Opportunity ID" value={selectedRow.opportunity_id} mono />
              <DetailRow label="Step" value={`${selectedRow.step_number} (${selectedRow.step_label})`} />
              {selectedRow.sms_prompt_context && (
                <DetailRow label="Decline reason" value={selectedRow.sms_prompt_context} multiline />
              )}
              <DetailRow label="Scheduled" value={new Date(selectedRow.created_at).toLocaleString()} />
              <DetailRow label="Fire-at" value={new Date(selectedRow.fire_at).toLocaleString()} />
              {selectedRow.last_attempt_at && (
                <DetailRow label="Marked lost at" value={new Date(selectedRow.last_attempt_at).toLocaleString()} />
              )}
              {selectedRow.error_message && (
                <DetailRow label="Error" value={selectedRow.error_message} multiline />
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </Card>
  )
}

function DetailRow({
  label,
  value,
  mono,
  multiline,
}: {
  label: string
  value: string
  mono?: boolean
  multiline?: boolean
}) {
  return (
    <div>
      <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
      <p className={`mt-0.5 ${mono ? 'font-mono text-xs' : ''} ${multiline ? 'whitespace-pre-wrap' : 'truncate'}`}>
        {value}
      </p>
    </div>
  )
}
